import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { createSectionView } from '../ViewTools';
import { BIMElement } from '../../core/types';

export class SectionViewTool extends BaseTool {
    id = 'view-section';
    name = 'Section';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private endPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.updatePreviewState({
                isValid: true,
                snapPoint: snapped,
                snapType: 'endpoint'
            });
        } else {
            this.endPoint = snapped;
            this.createSection();
            this.reset();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;
        
        const snapped = this.snapToGrid(point);
        
        if (this.startPoint) {
            // Apply ortho/angle snap
            const current = this.applyDirectionalSnap(snapped, this.startPoint);
            
            this.updatePreviewState({
                isValid: true,
                snapPoint: current,
                // In a real implementation, we would pass geometry here
            });
            
            // For now, we rely on the app to render the preview line based on this state
            // or we can set a specific preview object
            this.context.appStore.setToolPreview({
                type: 'section-line',
                start: [this.startPoint.x, this.startPoint.y, this.startPoint.z],
                end: [current.x, current.y, current.z]
            });
        } else {
             this.updatePreviewState({
                isValid: true,
                snapPoint: snapped
            });
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void {
        // No action needed
    }

    private createSection() {
        if (!this.context || !this.startPoint || !this.endPoint) return;

        // Calculate direction
        const direction = new THREE.Vector3()
            .subVectors(this.endPoint, this.startPoint)
            .normalize();
        
        // Create perpendicular vector for view direction (looking 'up' relative to the line)
        const viewDir = new THREE.Vector3(-direction.y, direction.x, 0);

        const view = createSectionView(
            'Section View',
            [this.startPoint.x, this.startPoint.y, this.startPoint.z],
            [viewDir.x, viewDir.y, viewDir.z]
        );

        // Register the view through the store action so the Project Browser
        // observes the new entry with a fresh map identity.
        this.context.bimStore.addView(view);

        // Also create a Section Marker element in the current view
        const marker: BIMElement = {
            id: uuidv4(),
            category: 'Views',
            type: 'Section Marker',
            parameters: {
                'View ID': view.id,
                'View Name': view.name,
                'View Id': view.id
            },
            constraints: [],
            geometryData: {
                start: [this.startPoint.x, this.startPoint.y, this.startPoint.z],
                end: [this.endPoint.x, this.endPoint.y, this.endPoint.z],
                normal: [viewDir.x, viewDir.y, 0],
                viewId: view.id,
                depth: 10
            }
        };
        
        this.context.bimStore.addElement(marker);
        
        console.log('Created Section View:', view);
    }

    private reset() {
        this.startPoint = null;
        this.endPoint = null;
        this.clearPreview();
    }
}
